import { reaction } from "mobx";
import { useLocalObservable } from "mobx-react-lite";
import { createContext, useCallback, useContext, useEffect } from "react";

import { useStructuredSearchStore } from "@/components/StructuredSearch";
import {
  AnalyticsQueryStore,
  createAnalyticsQueryState,
} from "@/components/RequestsAnalytics/analytics-query-store";

const AnalyticsQueryContext = createContext<AnalyticsQueryStore | null>(null);

export const AnalyticsQueryProvider = ({ children }: { children: React.ReactNode }) => {
  const structuredSearchStore = useStructuredSearchStore();

  const getCurrentQueryState = useCallback(
    () =>
      createAnalyticsQueryState({
        filters: structuredSearchStore.filters,
        dateRange: structuredSearchStore.dateRange,
        q: structuredSearchStore.q,
      }),
    [structuredSearchStore],
  );

  const analyticsQueryStore = useLocalObservable(
    () => new AnalyticsQueryStore(getCurrentQueryState),
  );

  useEffect(() => {
    const disposer = reaction(
      () => ({
        filters: structuredSearchStore.filters,
        dateRange: structuredSearchStore.dateRange,
        q: structuredSearchStore.q,
      }),
      analyticsQueryStore.syncAutomaticQuery,
      { fireImmediately: true },
    );

    return () => disposer();
  }, [analyticsQueryStore, structuredSearchStore]);

  return (
    <AnalyticsQueryContext.Provider value={analyticsQueryStore}>
      {children}
    </AnalyticsQueryContext.Provider>
  );
};

export const useAnalyticsQuery = (): AnalyticsQueryStore => {
  const context = useContext(AnalyticsQueryContext);
  if (!context) {
    throw new Error("useAnalyticsQuery must be used within AnalyticsQueryProvider");
  }
  return context;
};

export const useOptionalAnalyticsQuery = (): AnalyticsQueryStore | null =>
  useContext(AnalyticsQueryContext);

export {
  areAnalyticsQueriesEqual,
  createAnalyticsQueryState,
  serializeAnalyticsQueryState,
} from "./analytics-query-store";
export type { AnalyticsQueryState } from "./analytics-query-store";
